import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import { cn } from "@RHCommerceDev/utils/cn";
import { groupChildCategories } from "@RHCommerceDev/utils/parseCatalogNavigationData";
import { useEnv } from "hooks/useEnv";
import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@RHCommerceDev/unstyled-ui-components";
import memoize from "@RHCommerceDev/utils/memoize";
import { useAppId } from "hooks/useAppId";
import he from "he";
import getLocalizedUrl from "@RHCommerceDev/utils/getLocalizedUrl";

const NavigationDropdown = ({
  selectedCategory
}: {
  selectedCategory: NavigationElement | undefined;
}) => {
  const locale = useLocalization();
  const { FEATURE_SHOP_UI } = useEnv();
  const { isConcierge } = useAppId();

  // In concierge we want the second column to be open by default, hence we are selecting the first option from first column by default
  const [selectedSubcategoryIndex, setSelectedSubcategoryIndex] =
    useState<number>(isConcierge ? 0 : -1);
  const ref = useRef<HTMLDivElement>(null);
  const [elementOffset, setElementOffset] = useState<number>(0);
  const selectedSubcategory =
    selectedCategory?.childCategories?.[selectedSubcategoryIndex];

  const groupedCategories = groupChildCategories(
    selectedSubcategory?.childCategories
  );

  useEffect(() => {
    if (ref.current) {
      const { top } = ref.current.getBoundingClientRect();
      setElementOffset(Number(top ?? 0));
    }
  }, []);

  if (!selectedCategory) {
    return null;
  }

  return (
    <>
      <div
        ref={ref}
        className={cn("flex gap-20 bg-rh-mocha pb-10 pt-10 text-white", {
          "px-10": isConcierge
        })}
        style={{ height: `calc(100vh - ${elementOffset}px)` }}
      >
        {/* SUBCATEGORY COLUMN */}
        <div
          className="flex flex-col items-start gap-5 overflow-auto pr-3 md:gap-4 lg:gap-5"
          onClick={e => e.stopPropagation()}
        >
          {selectedCategory?.childCategories?.map((primaryItem, primaryIndex) =>
            !!primaryItem?.childCategories?.length ? (
              <button
                key={primaryIndex}
                onClick={() => {
                  setSelectedSubcategoryIndex(primaryIndex);
                }}
                onMouseEnter={() => {
                  if (isConcierge) {
                    setSelectedSubcategoryIndex(primaryIndex);
                  }
                }}
                data-analytics-parentCat={primaryItem.displayName}
                className="p-0 text-inherit"
              >
                <Typography
                  variant="default"
                  fontFamily="rhc"
                  fontWeight="thin"
                  className={cn(
                    "text-[13px] uppercase leading-[1.5em] tracking-[0.05em] lg:text-base",
                    {
                      "font-semibold tracking-[0.025em]":
                        selectedSubcategoryIndex === primaryIndex
                    }
                  )}
                  dangerouslySetInnerHTML={{
                    __html: he.decode(primaryItem?.displayName ?? "")
                  }}
                />
              </button>
            ) : (
              <a
                key={primaryIndex}
                href={getLocalizedUrl(
                  primaryItem?.targetUrl ?? "",
                  locale,
                  isConcierge
                )}
                className="text-inherit no-underline"
                onClick={e => {
                  const target = e?.target as HTMLElement;
                  const { id, className } = target;
                  document?.body?.dispatchEvent(
                    new CustomEvent("cta_click", {
                      detail: {
                        item: {
                          isHeaderNavigationNew: true,
                          topCat: selectedCategory.displayName,
                          parentCat: primaryItem?.displayName,
                          cat: primaryItem?.targetUrl,
                          catURL: primaryItem?.targetUrl,
                          class: className,
                          id: id
                        }
                      }
                    })
                  );
                }}
              >
                <Typography
                  variant="default"
                  fontFamily="rhc"
                  fontWeight="thin"
                  className={cn(
                    "text-[13px] uppercase leading-[1.5em] tracking-[0.05em] lg:text-base"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: he.decode(primaryItem?.displayName ?? "")
                  }}
                />
              </a>
            )
          )}
        </div>

        {/* THIRD COLUMN */}
        {selectedSubcategoryIndex !== -1 && (
          <div className="columns-2 gap-x-20 text-white [column-fill:_auto]">
            {groupedCategories?.map((group, groundIndex) => (
              <div
                key={groundIndex}
                className="mb-4 flex break-inside-avoid flex-col gap-4"
                onClick={e => e.stopPropagation()}
              >
                {group.header && (
                  <Typography
                    variant="default"
                    fontFamily="rhc"
                    className="text-[13px] font-semibold uppercase leading-[1.5em] tracking-[0.025em] text-inherit lg:text-base"
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    dangerouslySetInnerHTML={{
                      __html: he.decode(group.header?.displayName ?? "")
                    }}
                  />
                )}

                {group.children.map((tertiaryItem, tertiaryIndex) => (
                  <a
                    key={tertiaryIndex}
                    data-analytics-parentCat={tertiaryItem.displayName}
                    href={getLocalizedUrl(
                      tertiaryItem?.targetUrl ?? "",
                      locale,
                      isConcierge
                    )}
                    className="text-inherit no-underline"
                    onClick={e => {
                      const target = e?.target as HTMLElement;
                      const { id, className } = target;
                      document?.body?.dispatchEvent(
                        new CustomEvent("cta_click", {
                          detail: {
                            item: {
                              isHeaderNavigationNew: true,
                              topCat: selectedCategory.displayName,
                              parentCat: selectedSubcategory?.displayName,
                              cat: tertiaryItem?.displayName,
                              catURL: tertiaryItem?.targetUrl,
                              class: className,
                              id: id
                            }
                          }
                        })
                      );
                    }}
                  >
                    <Typography
                      variant="default"
                      fontFamily="rhc"
                      fontWeight="thin"
                      className="text-[13px] capitalize leading-[1.5em] tracking-[0.05em] lg:text-base"
                      dangerouslySetInnerHTML={{
                        __html: he.decode(tertiaryItem?.displayName ?? "")
                      }}
                    />
                  </a>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default memoize(NavigationDropdown);
